import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { ArrowLeft, Users, Camera, Check, Info } from 'lucide-react'
import type { FormData } from '@/hooks/useSubmissionWizard'

interface PeopleCheckStepProps {
  formData: FormData
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
  onNext: () => void
  onBack: () => void
}

type FlowState = 'people-question' | 'permission-question' | 'no-permission'

export function PeopleCheckStep({ formData, updateField, onNext, onBack }: PeopleCheckStepProps) {
  // Determine initial state based on existing form data
  const getInitialState = (): FlowState => {
    if (formData.includesPeople === true) {
      if (formData.hasModelPermission === false) {
        return 'no-permission'
      }
      return 'permission-question'
    }
    return 'people-question'
  }

  const [flowState, setFlowState] = useState<FlowState>(getInitialState)

  const handlePeopleAnswer = (includesPeople: boolean) => {
    updateField('includesPeople', includesPeople)
    if (!includesPeople) {
      // No people in photo - clear permission and proceed
      updateField('hasModelPermission', null)
      onNext()
    } else {
      // Show permission question
      setFlowState('permission-question')
    }
  }

  const handlePermissionAnswer = (hasPermission: boolean) => {
    updateField('hasModelPermission', hasPermission)
    if (hasPermission) {
      onNext()
    } else {
      // Show guidance message
      setFlowState('no-permission')
    }
  }

  const handleStartOver = () => {
    updateField('includesPeople', null)
    updateField('hasModelPermission', null)
    setFlowState('people-question')
  }

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <AnimatePresence mode="wait">
        {flowState === 'people-question' && (
          <motion.div
            key="people-question"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <div className="space-y-3">
              <h2 className="text-foreground font-display text-3xl font-bold md:text-4xl lg:text-5xl">
                Is there a person in this photo?
              </h2>
              <p className="text-foreground-muted text-lg">We need to know if your photo includes any people</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => handlePeopleAnswer(true)}
                className="border-border bg-surface hover:border-primary hover:bg-primary/5 flex flex-col items-center gap-4 rounded-lg border-2 p-6 transition-colors"
              >
                <Users className="text-foreground-muted h-12 w-12" />
                <span className="text-foreground text-lg font-medium">Yes</span>
                <span className="text-foreground-muted text-center text-sm">My photo includes one or more people</span>
              </button>

              <button
                type="button"
                onClick={() => handlePeopleAnswer(false)}
                className="border-border bg-surface hover:border-primary hover:bg-primary/5 flex flex-col items-center gap-4 rounded-lg border-2 p-6 transition-colors"
              >
                <Camera className="text-foreground-muted h-12 w-12" />
                <span className="text-foreground text-lg font-medium">No</span>
                <span className="text-foreground-muted text-center text-sm">No people in my photo</span>
              </button>
            </div>

            <div className="flex items-center gap-4">
              <Button type="button" variant="ghost" size="lg" onClick={onBack}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </div>
          </motion.div>
        )}

        {flowState === 'permission-question' && (
          <motion.div
            key="permission-question"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <div className="space-y-3">
              <h2 className="text-foreground font-display text-3xl font-bold md:text-4xl lg:text-5xl">
                Did you get permission?
              </h2>
              <p className="text-foreground-muted text-lg">
                Did you get permission from this person to use this photo?
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => handlePermissionAnswer(true)}
                className="border-border bg-surface hover:border-success hover:bg-success/5 flex flex-col items-center gap-4 rounded-lg border-2 p-6 transition-colors"
              >
                <Check className="text-success h-12 w-12" />
                <span className="text-foreground text-lg font-medium">Yes, I have permission</span>
                <span className="text-foreground-muted text-center text-sm">
                  The person(s) agreed to be in the photo
                </span>
              </button>

              <button
                type="button"
                onClick={() => handlePermissionAnswer(false)}
                className="border-border bg-surface hover:border-foreground-muted flex flex-col items-center gap-4 rounded-lg border-2 p-6 transition-colors"
              >
                <Users className="text-foreground-muted h-12 w-12" />
                <span className="text-foreground text-lg font-medium">No, not yet</span>
                <span className="text-foreground-muted text-center text-sm">I haven't asked for permission yet</span>
              </button>
            </div>

            <div className="flex items-center gap-4">
              <Button type="button" variant="ghost" size="lg" onClick={handleStartOver}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </div>
          </motion.div>
        )}

        {flowState === 'no-permission' && (
          <motion.div
            key="no-permission"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <div className="space-y-3">
              <h2 className="text-foreground font-display text-3xl font-bold md:text-4xl lg:text-5xl">
                Permission Required
              </h2>
            </div>

            {/* Friendly info message - not an error */}
            <div className="bg-accent/10 border-accent/20 space-y-4 rounded-xl border p-6">
              <div className="flex items-start gap-4">
                <div className="bg-accent/20 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                  <Info className="text-accent h-5 w-5" />
                </div>
                <div className="space-y-2">
                  <p className="text-foreground font-medium">
                    Please choose a different photo or get permission from the model before submitting.
                  </p>
                  <p className="text-foreground-muted text-sm">
                    Photos that include people require their permission to be submitted to the competition.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Button type="button" variant="outline" size="lg" onClick={handleStartOver}>
                <ArrowLeft className="h-5 w-5" />
                Choose Different Answer
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
